import { Component, OnInit, DOCUMENT, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ThemeService } from '../../_services/theme.service';
import { environment } from '../../../environments/environment';
import { UploadThemeModalComponent, ThemeDeltaRow, ThemeUploadData } from '../upload-theme-modal/upload-theme-modal.component';

interface ThemeMetadata {
  id: string;
  name: string;
  description: string;
  hasLight: boolean;
  hasDark: boolean;
}

interface ThemeJson {
  name?: string;
  displayName?: string;
  description?: string;
  version?: string;
  light?: Record<string, string>;
  dark?: Record<string, string>;
}

@Component({
  selector: 'app-theme-controls',
  imports: [FormsModule, UploadThemeModalComponent],
  templateUrl: './theme-controls.component.html',
  styleUrl: './theme-controls.component.css',
})
export class ThemeControlsComponent implements OnInit {
  private document = inject(DOCUMENT);

  availableThemes: ThemeMetadata[] = [];
  selectedTheme: string = 'neo';
  isVisible: boolean = environment.enableThemeSwitcher === true;

  // Additional controls
  // Auto-sequence gates programmatic navigation in the UI-new flow.
  // Default is ON to preserve current behavior unless explicitly turned off.
  autoSequence: string = 'on';
  logoEnabled: string = 'off';
  logoUrl: string = '';

  // Upload theme state
  showUploadModal = false;
  uploadMode: 'light' | 'dark' = 'light';
  uploadDeltaRows: ThemeDeltaRow[] = [];
  uploadError: string | null = null;
  uploadThemeName: string = '';
  uploadVersion: string = '';

  private pendingUploadTheme: ThemeJson | null = null;
  private currentThemeForComparison: ThemeJson | null = null;

  constructor(public themeService: ThemeService) {}

  ngOnInit(): void {
    // Subscribe to available themes
    this.themeService.availableThemes$.subscribe(themes => {
      this.availableThemes = themes;
    });

    // Subscribe to current theme
    this.themeService.currentTheme$.subscribe(themeId => {
      this.selectedTheme = themeId;
    });

    // Load saved preferences from localStorage
    this.loadPreferences();
  }

  onThemeChange(event: Event): void {
    const themeId = (event.target as HTMLSelectElement).value;
    this.selectedTheme = themeId;
    this.themeService.setTheme(themeId);
  }

  onAutoSequenceChange(event: Event): void {
    this.autoSequence = (event.target as HTMLSelectElement).value;
    try {
      localStorage.setItem('autoSequence', this.autoSequence);
    } catch {
      // ignore (some environments block storage writes)
    }

    // Optional broadcast so other components can react immediately if needed.
    try {
      window.dispatchEvent(
        new CustomEvent('ui:new:autoSequenceChanged', {
          detail: { enabled: this.autoSequence === 'on' },
        }),
      );
    } catch {
      // ignore
    }
  }

  onLogoToggle(event: Event): void {
    this.logoEnabled = (event.target as HTMLSelectElement).value;
    localStorage.setItem('logoEnabled', this.logoEnabled);
    this.updateLogoDisplay();
  }

  onLogoUrlChange(): void {
    localStorage.setItem('logoUrl', this.logoUrl);
    this.updateLogoDisplay();
  }

  private updateLogoDisplay(): void {
    // Signal change to the window for other components to listen
    const logoUrl = this.logoEnabled === 'on' ? this.logoUrl : null;
    window.dispatchEvent(new CustomEvent('logo:updated', { detail: { logoUrl } }));
  }

  private loadPreferences(): void {
    // Load saved preferences
    try {
      const savedAutoSequence = localStorage.getItem('autoSequence');
      if (savedAutoSequence === 'on' || savedAutoSequence === 'off') {
        this.autoSequence = savedAutoSequence;
      }
    } catch {
      // ignore
    }

    try {
      const savedLogoEnabled = localStorage.getItem('logoEnabled');
      if (savedLogoEnabled) {
        this.logoEnabled = savedLogoEnabled;
      }
    } catch {
      // ignore
    }

    try {
      const savedLogoUrl = localStorage.getItem('logoUrl');
      if (savedLogoUrl) {
        this.logoUrl = savedLogoUrl;
      }
    } catch {
      // ignore
    }
  }

  // ── Upload Theme Methods ──────────────────────────────────────────────────

  /** Trigger the hidden file input */
  onUploadThemeClick(): void {
    const input = this.document.getElementById('themeUploadInput') as HTMLInputElement | null;
    input?.click();
  }

  /** Handle file selection: parse JSON, fetch current theme, build delta */
  async onThemeFileSelected(event: Event): Promise<void> {
    this.uploadError = null;
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const uploaded = JSON.parse(text) as ThemeJson;

      // Validate structure
      if (!uploaded.light && !uploaded.dark) {
        this.uploadError = 'Invalid theme file – missing "light" / "dark" keys.';
        this.resetFileInput(event);
        return;
      }

      // Extract theme metadata
      this.uploadThemeName = uploaded.name || uploaded.displayName || '';
      this.uploadVersion = uploaded.version || 'Will be set by API';

      // Fetch the current theme for comparison
      const currentThemeId = this.themeService.getCurrentThemeId();
      let current: ThemeJson = { light: {}, dark: {} };
      try {
        const baseUrl = this.document.baseURI || '/';
        const url = new URL(`assets/themes/${currentThemeId}.json`, baseUrl).toString();
        const res = await fetch(url, { cache: 'no-cache' });
        if (res.ok) current = await res.json();
      } catch {
        // If the fetch fails, compare against empty (all vars appear as "new")
      }

      this.pendingUploadTheme = uploaded;
      this.currentThemeForComparison = current;
      this.uploadMode = 'light';
      this.rebuildUploadDelta();
      this.showUploadModal = true;
    } catch {
      this.uploadError = 'Failed to parse the file. Ensure it is valid JSON.';
    }

    this.resetFileInput(event);
  }

  /** Switch the diff view between light and dark mode */
  onUploadModeChange(mode: 'light' | 'dark'): void {
    this.uploadMode = mode;
    this.rebuildUploadDelta();
  }

  /** User changed the theme name in the modal */
  onUploadThemeNameChange(themeName: string): void {
    this.uploadThemeName = themeName;
  }

  /** Apply the uploaded theme (in a real app, this would call an API) */
  onConfirmUpload(data: ThemeUploadData): void {
    if (!this.pendingUploadTheme) return;

    // TODO: Call API to upload theme with data.themeName and data.version
    // For now, we'll just log and close the modal
    console.log('Upload confirmed:', {
      themeName: data.themeName,
      version: data.version,
      theme: this.pendingUploadTheme,
    });

    // In a real implementation, you would:
    // 1. Call an API endpoint to save the theme
    // 2. The API would return the actual version number
    // 3. Update the available themes list
    // 4. Optionally switch to the newly uploaded theme

    this.closeUploadModal();
    console.log(`Theme "${data.themeName}" upload prepared!\n\nThis would be sent to the API for processing.`);
  }

  /** Discard the upload */
  onCancelUpload(): void {
    this.closeUploadModal();
  }

  // ── Upload Delta Helpers ──────────────────────────────────────────────────

  private rebuildUploadDelta(): void {
    if (!this.pendingUploadTheme || !this.currentThemeForComparison) {
      this.uploadDeltaRows = [];
      return;
    }

    const mode = this.uploadMode;
    const currentVars = (this.currentThemeForComparison[mode] as Record<string, string>) ?? {};
    const uploadedVars = (this.pendingUploadTheme[mode] as Record<string, string>) ?? {};

    // Union of all CSS variable names from both themes
    const allNames = new Set<string>([
      ...Object.keys(currentVars).filter(v => v.startsWith('--')),
      ...Object.keys(uploadedVars).filter(v => v.startsWith('--')),
    ]);

    this.uploadDeltaRows = Array.from(allNames)
      .map((varName): ThemeDeltaRow => {
        const before = currentVars[varName] ?? '';
        const after = uploadedVars[varName] ?? '';
        const hasChange = before !== after;
        return {
          variable: varName,
          label: this.formatDeltaLabel(varName),
          before,
          after,
          changes: hasChange ? after : '',
          isNew: !currentVars[varName] && !!uploadedVars[varName],
          isRemoved: !!currentVars[varName] && !uploadedVars[varName],
          hasChange,
        };
      })
      .sort((a, b) => {
        // Changed rows first, then alphabetical
        if (a.hasChange !== b.hasChange) return a.hasChange ? -1 : 1;
        return a.variable.localeCompare(b.variable);
      });
  }

  private formatDeltaLabel(varName: string): string {
    return varName
      .replace(/^--/, '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  private closeUploadModal(): void {
    this.showUploadModal = false;
    this.pendingUploadTheme = null;
    this.currentThemeForComparison = null;
    this.uploadDeltaRows = [];
    this.uploadError = null;
    this.uploadThemeName = '';
    this.uploadVersion = '';
  }

  private resetFileInput(event: Event): void {
    (event.target as HTMLInputElement).value = '';
  }

  // ── Preview Upload Methods ────────────────────────────────────────────────

  /** Trigger the hidden file input for preview upload */
  onPreviewUploadClick(): void {
    const input = this.document.getElementById('themePreviewUploadInput') as HTMLInputElement | null;
    input?.click();
  }

  /** Handle preview upload file selection: parse JSON, store in localStorage, and apply */
  async onPreviewThemeFileSelected(event: Event): Promise<void> {
    this.uploadError = null;
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const uploaded = JSON.parse(text) as ThemeJson;

      // Validate structure
      if (!uploaded.light && !uploaded.dark) {
        this.uploadError = 'Invalid theme file – missing "light" / "dark" keys.';
        this.resetFileInput(event);
        return;
      }

      // Extract theme name from filename (remove .json extension)
      const fileName = file.name.replace(/\.json$/i, '');
      const themeName = uploaded.name || uploaded.displayName || fileName;
      const themeId = `${themeName.toLowerCase().replace(/\s+/g, '-')}-local`;

      // Store theme in localStorage
      try {
        localStorage.setItem(`theme_${themeId}`, text);
        console.log(`Theme stored in localStorage as: theme_${themeId}`);
      } catch (e) {
        this.uploadError = 'Failed to store theme in localStorage. Storage may be full.';
        this.resetFileInput(event);
        return;
      }

      // Update the available themes list to include this local theme
      const localThemeMetadata: ThemeMetadata = {
        id: themeId,
        name: `${themeName} - local`,
        description: uploaded.description || 'Locally uploaded theme',
        hasLight: !!uploaded.light,
        hasDark: !!uploaded.dark,
      };

      // Add to ThemeService's available themes
      this.themeService.addLocalTheme(localThemeMetadata, uploaded);

      // Set this theme as active
      await this.themeService.setTheme(themeId);

      console.log(`Preview theme "${themeName}" loaded successfully as "${themeId}"`);
    } catch (e) {
      this.uploadError = 'Failed to parse the file. Ensure it is valid JSON.';
      console.error('Preview upload error:', e);
    }

    this.resetFileInput(event);
  }
}
